import { useEffect, useState } from "react";
import { Box, useTheme } from "@mui/system";
import useStyles from "./styles";
import {
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Divider,
  Grid,
} from "@mui/material";
import {
  Instagram,
  MailOutlined,
  PhotoOutlined,
  WhatsApp,
  Language,
  Business,
  LocationOn,
  Description,
  ApartmentOutlined,
} from "@mui/icons-material";
import api from "../../../../../services/api";

export default function HeaderReport() {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [snackbar, setSnackbar] = useState(null);
  const [editConfig, setEditConfig] = useState(true);

  const [errorWhatsApp, setErrorWhatsApp] = useState(false);
  const [errorEmail, setErrorEmail] = useState(false);

  const initialData = {
    imagem: "",
    whatsapp: "",
    instagram: "",
    email: "",
    site: "",
    cnpj: "",
    endereco: "",
    razaoSocial: "",
    inscricaoEstadual: "",
  };

  const [headerData, setHeaderData] = useState(initialData);
  const [editHeader, setEditHeader] = useState(initialData);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setEditHeader(headerData);
  }, [headerData]);

  /* --- MÁSCARAS E VALIDAÇÕES --- */

  const regexTelefone = /^\(\d{2}\)\s\d{4,5}-\d{4}$/;

  const maskDynamicPhone = (value) => {
    const cleaned = value?.replace(/\D/g, "").slice(0, 11);
    if (cleaned.length <= 10) {
      return cleaned
        ?.replace(/(\d{2})(\d)/, "($1) $2")
        ?.replace(/(\d{4})(\d)/, "$1-$2");
    }
    return cleaned
      ?.replace(/(\d{2})(\d)/, "($1) $2")
      ?.replace(/(\d{5})(\d)/, "$1-$2");
  };

  const validatePhone = (phone) =>
    !phone || regexTelefone.test(maskDynamicPhone(phone));

  const cleanPhone = (value) => value?.replace(/\D/g, "");

  const maskCNPJ = (value) => {
    return value
      ?.replace(/\D/g, "")
      ?.replace(/^(\d{2})(\d)/, "$1.$2")
      ?.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      ?.replace(/\.(\d{3})(\d)/, ".$1/$2")
      ?.replace(/(\d{4})(\d)/, "$1-$2")
      ?.substring(0, 18);
  };

  const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const validateEmail = (email) => !email || regexEmail.test(email);

  const regexInstagram = /^@?([a-zA-Z0-9._]{1,30})$/;
  const validateInstagram = (username) => {
    if (!username) return "";
    if (!username.startsWith("@")) username = `@${username}`;
    return regexInstagram.test(username) ? username : username?.trim();
  };

  const handleInstagramChange = (e) => {
    const input = validateInstagram(e?.target?.value);
    setEditHeader((prev) => ({ ...prev, instagram: input }));
  };

  const handleChange = (prop) => (event) => {
    setEditHeader({ ...editHeader, [prop]: event.target.value });
  };

  /* --- INTEGRAÇÃO API --- */

  const getData = async () => {
    try {
      const response = await api.post("/interno/find/generalSetting", {
        product: "usageReport",
      });

      if (response?.data) {
        const mergedData = { ...initialData, ...response.data };
        setHeaderData(mergedData);

        const isWhatsAppValid = validatePhone(mergedData.whatsapp);
        const isEmailValid = validateEmail(mergedData.email?.trim());
        setErrorWhatsApp(!isWhatsAppValid);
        setErrorEmail(!isEmailValid);
      }
    } catch (error) {
      setSnackbar({
        children: "Erro: Não possível se conectar ao servidor",
        severity: "error",
      });
    }
  };

  const handleDados = async () => {
    const isWhatsAppValid = validatePhone(editHeader?.whatsapp);
    const isEmailValid = validateEmail(editHeader?.email?.trim());

    setErrorWhatsApp(!isWhatsAppValid);
    setErrorEmail(!isEmailValid);

    if (!isWhatsAppValid || !isEmailValid) return;

    try {
      const settings = {
        ...editHeader,
        whatsapp: cleanPhone(editHeader?.whatsapp),
        instagram: editHeader?.instagram?.trim(),
        email: editHeader?.email?.trim(),
      };

      const response = await api.post("/interno/upsert/generalSettings", {
        id: editHeader?.id || "",
        product: "usageReport",
        settings: settings,
      });

      setHeaderData(settings);
      setSnackbar({
        children: "Configurações salvas",
        severity: "success",
      });
      setEditConfig(true);
    } catch (error) {
      console.log(error);
      setSnackbar({
        children: "Erro ao tentar salvar as configurações",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Grid container spacing={2}>
        {/* --- COLUNA ESQUERDA: FORMULÁRIO --- */}
        <Grid item xs={12} lg={6}>
          <Paper
            elevation={theme.palette.mode === "dark" ? 0 : 3}
            sx={styles.paper}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Editar Cabeçalho
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Razão Social"
                  size="small"
                  sx={styles.textfield}
                  value={editHeader.razaoSocial}
                  onChange={handleChange("razaoSocial")}
                  disabled={editConfig}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Business sx={{ color: editConfig && "grey.400" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Link da Logo (URL)"
                  size="small"
                  sx={styles.textfield}
                  value={editHeader.imagem}
                  onChange={handleChange("imagem")}
                  disabled={editConfig}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhotoOutlined
                          sx={{ color: editConfig && "grey.400" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="CNPJ"
                  size="small"
                  sx={styles.textfield}
                  value={maskCNPJ(editHeader.cnpj || "")}
                  onChange={(e) =>
                    setEditHeader({
                      ...editHeader,
                      cnpj: maskCNPJ(e.target.value),
                    })
                  }
                  disabled={editConfig}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ApartmentOutlined
                          sx={{ color: editConfig && "grey.400" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Inscrição Estadual"
                  size="small"
                  sx={styles.textfield}
                  value={editHeader.inscricaoEstadual}
                  onChange={handleChange("inscricaoEstadual")}
                  disabled={editConfig}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Description sx={{ color: editConfig && "grey.400" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Endereço Completo"
                  size="small"
                  sx={styles.textfield}
                  value={editHeader.endereco}
                  onChange={handleChange("endereco")}
                  disabled={editConfig}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn sx={{ color: editConfig && "grey.400" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ mb: 1 }}
                >
                  Contatos
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Site"
                  size="small"
                  sx={styles.textfield}
                  placeholder="www.exemplo.com.br"
                  value={editHeader.site}
                  onChange={handleChange("site")}
                  disabled={editConfig}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Language sx={{ color: editConfig && "grey.400" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="E-mail"
                  size="small"
                  sx={styles.textfield}
                  value={editHeader.email}
                  onChange={(e) => {
                    handleChange("email")(e);
                    setErrorEmail(false);
                  }}
                  error={errorEmail}
                  helperText={errorEmail ? "E-mail inválido" : ""}
                  disabled={editConfig}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlined
                          sx={{ color: editConfig && "grey.400" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="WhatsApp"
                  size="small"
                  sx={styles.textfield}
                  placeholder="(99) 99999-9999"
                  value={maskDynamicPhone(editHeader.whatsapp || "")}
                  onChange={(e) => {
                    const masked = maskDynamicPhone(e.target.value);
                    setEditHeader({ ...editHeader, whatsapp: masked });
                    setErrorWhatsApp(false);
                  }}
                  error={errorWhatsApp}
                  helperText={errorWhatsApp ? "Inválido" : ""}
                  disabled={editConfig}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WhatsApp sx={{ color: editConfig && "grey.400" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Instagram"
                  size="small"
                  sx={styles.textfield}
                  value={editHeader.instagram}
                  onChange={handleInstagramChange}
                  placeholder="@"
                  disabled={editConfig}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Instagram sx={{ color: editConfig && "grey.400" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
              {editConfig ? (
                <Button
                  variant="contained"
                  onClick={() => setEditConfig(false)}
                >
                  Editar
                </Button>
              ) : (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEditConfig(true);
                      setEditHeader(headerData);
                      setErrorEmail(false);
                      setErrorWhatsApp(false);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button variant="contained" onClick={handleDados}>
                    Salvar
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* --- COLUNA DIREITA: PREVIEW (FOLHA A4) --- */}
        <Grid item xs={12} lg={6}>
          <Paper sx={styles.previewContainer}>
            {/* Papel simulando A4 */}
            <Box sx={styles.previewPaper}>
              {/* CABEÇALHO DO PDF */}
              <Box sx={styles.previewTopRow}>
                {/* Logo */}
                <Box sx={styles.previewLogoContainer}>
                  {editHeader.imagem ? (
                    <Box
                      component="img"
                      src={editHeader.imagem}
                      alt="Logo"
                      sx={styles.previewLogo}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "50px",
                        bgcolor: "#f0f0f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                        color: "#999",
                        border: "1px dashed #ccc",
                      }}
                    >
                      LOGO AQUI
                    </Box>
                  )}
                </Box>

                {/* Dados da Empresa */}
                <Box sx={styles.previewCompanyInfo}>
                  {!!editHeader.razaoSocial && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "12px",
                        color: theme.palette.colors.black.darker,
                      }}
                    >
                      {editHeader.razaoSocial.toUpperCase()}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "10px", color: "#333", display: "block" }}
                  >
                    CNPJ: {editHeader.cnpj || "N/A"}
                    {editHeader.inscricaoEstadual
                      ? ` | IE: ${editHeader.inscricaoEstadual}`
                      : ""}
                  </Typography>
                  {editHeader.endereco && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "10px",
                        color: "#333",
                        display: "block",
                      }}
                    >
                      {editHeader.endereco}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Contatos (Site | Email | Zap | Insta) */}
              <Typography
                variant="caption"
                display="block"
                sx={styles.previewContactRow}
              >
                {[
                  editHeader.site,
                  editHeader.email,
                  editHeader.whatsapp
                    ? maskDynamicPhone(editHeader.whatsapp)
                    : null,
                  editHeader.instagram,
                ]
                  .filter(Boolean)
                  .join(" | ")}
              </Typography>

              <Divider sx={{ borderColor: "#78909C" }} />

              {/* Marca d'água no meio da folha */}
              <Typography sx={styles.watermark}>Meu Relatório</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  );
}
